import { DataSourceProvider } from "common";
import React from "react";

export enum BucketObjectTypes {
  Folder = "folder",
  File = "file",
}

export interface BucketObject {
  id?: string;
  name: string;
  displayName?: string;
  bucketName?: string;
  path?: string;
  size: number | null;
  dateModified: string | null;
  type: BucketObjectTypes;
  baseSpaceFileType?: "Project" | "Dataset" | "File";
}

export const getParentDirectory = (
  path: string | undefined
): string | undefined => {
  if (!path) return undefined;

  // Remove trailing slash if it exists
  const trimmed = path.endsWith("/") ? path.slice(0, -1) : path;

  // Find last slash
  const lastSlash = trimmed.lastIndexOf("/");

  if (lastSlash === -1) {
    return undefined; // no parent directory
  }

  return trimmed.substring(0, lastSlash + 1); // keep trailing slash
};

export const getBucketIdentifier = (dataSourceProvider: DataSourceProvider) => {
  if (dataSourceProvider === DataSourceProvider.Amazon) return "s3://";
  else if (dataSourceProvider === DataSourceProvider.Google) return "gs://";
  else if (dataSourceProvider === DataSourceProvider.RemoteStorage)
    return "remote://";
  else return "";
};

export const buildFullPath = (
  obj: any,
  dataSourceProvider: DataSourceProvider
): string => {
  if (dataSourceProvider === DataSourceProvider.SSH) {
    return obj.bucketName + (obj.prefix || "");
  }
  // remove trailing slashes from path
  const cleanPath = obj.bucketName?.replace(/\/+$/, "");
  // remove leading slashes from name
  const cleanName = obj.prefix?.replace(/^\/+/, "");
  return cleanName
    ? `${getBucketIdentifier(dataSourceProvider)}${cleanPath}/${cleanName}`
    : `${getBucketIdentifier(dataSourceProvider)}${cleanPath}`;
};

export const getDisplayableObjectName = (fullPrefix: string) => {
  const pathList = fullPrefix.split("/");

  const displayableName =
    pathList[pathList.length - (pathList[pathList.length - 1] === "" ? 2 : 1)];

  return displayableName;
};

export const getDisplayablePath = (
  bucketName: string,
  fullPrefix: string,
  dataSourceProvider: DataSourceProvider
) => {
  const pathList = fullPrefix.split("/").filter(part => part !== "");
  pathList.pop();
  const finalPath = pathList.join("/");
  const base = `${getBucketIdentifier(dataSourceProvider)}${bucketName ?? ""}`;

  return finalPath ? `${base}/${finalPath}` : base;
};

export const formatBucketObjects = (
  folderList: any,
  dataSourceProvider: DataSourceProvider
) => {
  return [
    ...(folderList?.CommonPrefixes || []).map(object => ({
      id: object.Prefix,
      displayName: getDisplayableObjectName(object.Prefix),
      name: object.Prefix,
      bucketName: folderList?.Name,
      path: getDisplayablePath(
        folderList?.Name,
        object.Prefix,
        dataSourceProvider
      ),
      size: null,
      dateModified: null,
      type: "folder",
    })),
    ...(folderList?.Contents || []).map(object => ({
      id: object.Key,
      displayName: getDisplayableObjectName(object.Key),
      name: object.Key,
      bucketName: folderList?.Name,
      path: getDisplayablePath(
        folderList?.Name,
        object.Key,
        dataSourceProvider
      ),
      size: object.Size,
      dateModified: object.LastModified,
      type: "file",
    })),
  ];
};

export const keyOf = (obj: BucketObject) => `${obj.path ?? ""}${obj.name}`;

export const FileBrowserPanel: React.FC<
  React.PropsWithChildren<{ className?: string; dataTestId?: string }>
> = ({ children, className = "", dataTestId = "" }) => (
  <div
    data-testid={dataTestId ?? undefined}
    className={`rounded-xl border bg-white shadow-sm ${className}`}>
    {children}
  </div>
);

export const FileBrowserBadge: React.FC<{ count: number }> = ({ count }) => (
  <span className="ml-2 inline-flex min-w-[1.5rem] items-center justify-center rounded-full bg-gray-900 px-2 text-xs font-semibold text-white">
    {count}
  </span>
);

export function globStringToRegex(pattern: string): RegExp {
  let regexStr = "^"; // start anchor, forces match from beginning of string
  let i = 0;

  while (i < pattern.length) {
    const char = pattern[i];

    if (char === "*") {
      regexStr += ".*"; // * means "match anything, zero or more characters"
    } else if (char === "?") {
      regexStr += "."; // ? means "match exactly one character"
    } else {
      // escape regex special chars so they are treated literally
      regexStr += char.replace(/[.+^${}()|[\]\\]/g, "\\$&");
    }

    i++;
  }

  regexStr += "$"; // end anchor, forces match to the end of string
  return new RegExp(regexStr);
}
